/**
 * "Changes waiting for approval": every PRODUCTION change is a stored proposal plus a four-eyes
 * request. A second person approves or rejects it (the existing approvals endpoint), then someone
 * holding the same permission applies it. The requester can cancel it. Shown on all three pages.
 */

import { CheckCheck, CircleSlash, Clock, ShieldCheck, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DialogFooter } from '@/components/ui/dialog';
import { api } from '../../api';
import type { ChangeKind, IntegrationChange } from '../../api/areas/integrations';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { DataError } from '../../components/DataError';
import { formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  ChangeDialog,
  ConfirmCheck,
  ConnectionLink,
  EnvironmentBanner,
  FieldChanges,
  IntegrationError,
  Panel,
  ReasonField,
  StaffName,
  StatusBadge,
  useIntegrationContext,
  When,
} from './shared';

export const CONNECTION_CHANGE_KINDS: readonly ChangeKind[] = ['CONNECTION_UPDATE', 'ENVIRONMENT_VERSION', 'CREDENTIAL_VERSION', 'CERTIFICATE_VERSION'];
export const TLD_CHANGE_KINDS: readonly ChangeKind[] = ['TLD_CHANNEL', 'TLD_CHANNEL_BATCH'];
export const PROVIDER_CHANGE_KINDS: readonly ChangeKind[] = ['PROVIDER_SETTING_VERSION', 'SPONSORING_REGISTRAR'];
/** Cài đặt › Module (ADR-025): a module instance's version waiting for approval. */
export const MODULE_CHANGE_KINDS: readonly ChangeKind[] = ['MODULE_INSTANCE_VERSION'];

const PAGE_OF: Record<ChangeKind, string> = {
  CONNECTION_UPDATE: '/settings/registries',
  ENVIRONMENT_VERSION: '/settings/registries',
  CREDENTIAL_VERSION: '/settings/registries',
  CERTIFICATE_VERSION: '/settings/registries',
  TLD_CHANNEL: '/settings/tlds',
  TLD_CHANNEL_BATCH: '/settings/tlds',
  PROVIDER_SETTING_VERSION: '/settings/vnnic',
  SPONSORING_REGISTRAR: '/settings/vnnic',
  MODULE_INSTANCE_VERSION: '/settings?tab=modules',
};

function ChangeTarget({ change }: { change: IntegrationChange }) {
  const { t } = useTranslation('integrations');
  const target = change.target;
  if (change.kind === 'TLD_CHANNEL' && target.tld) {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono font-medium">.{target.tld.zone}</span>
        {target.connection ? (
          <>
            <span aria-hidden="true">→</span>
            <ConnectionLink connection={target.connection} />
          </>
        ) : null}
      </span>
    );
  }
  if (change.kind === 'TLD_CHANNEL_BATCH') {
    const zones = target.tlds ?? [];
    const shown = zones.slice(0, 8);
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium">{t('changes.tldCount', { count: target.tld_count ?? zones.length })}</span>
        {shown.length ? (
          <span className="font-mono text-xs" title={zones.map((z) => `.${z.zone}`).join(' ')}>
            ({shown.map((z) => `.${z.zone}`).join(' ')}
            {zones.length > shown.length ? ` ${t('changes.tldMore', { count: zones.length - shown.length })}` : ''})
          </span>
        ) : null}
        {target.connection ? (
          <>
            <span aria-hidden="true">→</span>
            <ConnectionLink connection={target.connection} />
          </>
        ) : null}
      </span>
    );
  }
  if (target.module) {
    const m = target.module;
    const to = `/settings?${new URLSearchParams({ tab: 'modules', branch: m.branch, module: m.module, instance: m.instance_key, pane: 'config' }).toString()}`;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <Link to={to} className="font-medium text-primary hover:underline">
          {m.display_name ?? m.instance_key}
        </Link>
        <code className="text-xs text-muted-foreground">
          {m.branch}/{m.module}/{m.instance_key}
        </code>
      </span>
    );
  }
  if (target.provider) return <span className="font-medium">{t(`provider.name.${target.provider}`)}</span>;
  if (target.connection) return <ConnectionLink connection={target.connection} />;
  return <span className="text-muted-foreground">—</span>;
}

function ChangeCard({ change, onChanged }: { change: IntegrationChange; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const [dialog, setDialog] = useState<'decide' | 'apply' | 'cancel' | null>(null);
  // Refresh the list only when the dialog closes: refreshing earlier would unmount this card
  // (an applied or rejected change leaves the list) before the operator sees the result.
  const changed = useRef(false);
  const markChanged = () => {
    changed.current = true;
  };
  const close = () => {
    setDialog(null);
    if (changed.current) {
      changed.current = false;
      onChanged();
    }
  };
  const a = change.approval;
  const mine = a.status === 'PENDING' && !a.can_decide && a.can_cancel;
  return (
    <li className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{t(`changes.kind.${change.kind}`)}</span>
            <StatusBadge group="approvalStatus" value={a.status} />
            {change.target.version !== null ? <span className="text-xs text-muted-foreground">{t('changes.version', { version: change.target.version })}</span> : null}
          </div>
          <div className="text-sm">
            <ChangeTarget change={change} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {a.can_decide ? (
            <Button size="sm" onClick={() => setDialog('decide')}>
              <ShieldCheck /> {t('changes.decide')}
            </Button>
          ) : null}
          {a.can_self_approve ? (
            <Button size="sm" onClick={() => setDialog('decide')}>
              <ShieldCheck /> {t('changes.selfApprove')}
            </Button>
          ) : null}
          {a.can_apply ? (
            <Button size="sm" onClick={() => setDialog('apply')} disabled={change.stale}>
              <CheckCheck /> {t('changes.apply')}
            </Button>
          ) : null}
          {a.can_cancel ? (
            <Button size="sm" variant="outline" onClick={() => setDialog('cancel')}>
              <CircleSlash /> {t('changes.cancel')}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {t('changes.requestedBy')} <StaffName staff={a.requested_by} /> · <When at={a.requested_at} />
        </span>
        {a.decided_by ? (
          <span className="inline-flex items-center gap-1">
            {t('changes.decidedBy')} <StaffName staff={a.decided_by} /> · <When at={a.decided_at} />
          </span>
        ) : null}
        {a.status === 'PENDING' || a.status === 'APPROVED' ? (
          <span className="inline-flex items-center gap-1">
            <Clock className="size-3" aria-hidden="true" /> {t('changes.expires', { when: formatRelative(a.expires_at) })}
          </span>
        ) : null}
      </div>
      <p className="text-sm">
        <span className="text-muted-foreground">{t('changes.reason')}:</span> {change.reason}
      </p>
      <FieldChanges changes={change.changes} compact />
      {change.stale ? <Callout tone="warn">{t('changes.stale')}</Callout> : null}
      {mine ? (
        <p className="text-xs text-muted-foreground">
          {a.can_self_approve ? t('changes.mineSelfApprove') : t('changes.mine', { permission: t(`permission.${a.action}`) })}
        </p>
      ) : null}
      {a.self_approved ? <p className="text-xs text-muted-foreground">{t('changes.selfApproved')}</p> : null}
      {a.status === 'PENDING' && !mine && !a.can_decide ? <p className="text-xs text-muted-foreground">{t('changes.waiting', { permission: t(`permission.${a.action}`) })}</p> : null}
      {a.status === 'APPROVED' && !a.can_apply ? <p className="text-xs text-muted-foreground">{t('changes.approvedWaiting', { permission: t(`permission.${a.action}`) })}</p> : null}

      <ChangeDialog
        open={dialog === 'decide'}
        onOpenChange={(o) => (o ? setDialog('decide') : close())}
        title={a.can_self_approve ? t('changes.selfApproveTitle') : t('changes.decideTitle')}
        description={a.can_self_approve ? t('changes.selfApproveDescription') : t('changes.decideDescription')}
        wide
      >
        <DecideForm change={change} onClose={close} onDone={markChanged} />
      </ChangeDialog>
      <ChangeDialog open={dialog === 'apply'} onOpenChange={(o) => (o ? setDialog('apply') : close())} title={t('changes.applyTitle')} description={t('changes.applyDescription')} wide>
        <ApplyForm change={change} onClose={close} onDone={markChanged} />
      </ChangeDialog>
      <ChangeDialog open={dialog === 'cancel'} onOpenChange={(o) => (o ? setDialog('cancel') : close())} title={t('changes.cancelTitle')} description={t('changes.cancelDescription')}>
        <CancelForm change={change} onClose={close} onDone={markChanged} />
      </ChangeDialog>
    </li>
  );
}

/** What is being decided, applied or cancelled: target, environment, who proposed / approved it,
 * the reason and every field change. */
function ChangeSummary({ change }: { change: IntegrationChange }) {
  const { t } = useTranslation('integrations');
  const a = change.approval;
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{t(`changes.kind.${change.kind}`)}</span>
        {change.target.version !== null ? <span className="text-xs text-muted-foreground">{t('changes.version', { version: change.target.version })}</span> : null}
      </div>
      <div className="text-sm">
        <ChangeTarget change={change} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {t('changes.requestedBy')} <StaffName staff={a.requested_by} /> · <When at={a.requested_at} />
        </span>
        {a.decided_by ? (
          <span className="inline-flex items-center gap-1">
            {t('changes.decidedBy')} <StaffName staff={a.decided_by} /> · <When at={a.decided_at} />
          </span>
        ) : null}
      </div>
      <p className="text-sm">
        <span className="text-muted-foreground">{t('changes.reason')}:</span> {change.reason}
      </p>
      <FieldChanges changes={change.changes} />
    </div>
  );
}

function DecideForm({ change, onClose, onDone }: { change: IntegrationChange; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [approve, setApprove] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [applyNow, setApplyNow] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const selfApproval = !!change.approval.can_self_approve;
  const [totp, setTotp] = useState('');
  const decide = useWrite((body: { approve: boolean; reason: string; totp?: string }, opts) => api.decideIntegrationChange(change.approval.id, body, opts));
  const apply = useWrite((body: { reason: string | null }, opts) => api.applyIntegrationChange(change.approval.id, body, opts));
  const [finished, setFinished] = useState<'approved' | 'applied' | 'rejected' | null>(null);
  const submit = async () => {
    if (approve === null) return;
    const res = decide.state.phase === 'done' ? decide.state.result : await decide.run({ approve, reason: reason.trim(), ...(selfApproval ? { totp: totp.trim() } : {}) });
    setTotp('');
    if (!res) return;
    if (res.status === 'REJECTED') {
      setFinished('rejected');
      onDone();
      return;
    }
    if (applyNow && !change.stale) {
      const applied = await apply.run({ reason: reason.trim() || null });
      setFinished(applied ? 'applied' : 'approved');
    } else {
      setFinished('approved');
    }
    onDone();
  };
  if (finished) {
    return (
      <div className="space-y-4">
        <Callout tone={finished === 'rejected' ? 'neutral' : 'ok'} title={t(`changes.finished.${finished}`)}>
          {finished === 'approved' ? t('changes.finishedApprovedHint') : null}
        </Callout>
        {apply.state.phase === 'error' ? <IntegrationError error={apply.state.error} /> : null}
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {change.environment ? <EnvironmentBanner env={change.environment} message={t('changes.bannerDecide')} /> : null}
      <ChangeSummary change={change} />
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('changes.decision')}>
        <Button type="button" variant={approve === true ? 'default' : 'outline'} onClick={() => setApprove(true)} aria-pressed={approve === true}>
          <ThumbsUp /> {t('changes.approve')}
        </Button>
        <Button type="button" variant={approve === false ? 'destructive' : 'outline'} onClick={() => setApprove(false)} aria-pressed={approve === false}>
          <ThumbsDown /> {t('changes.reject')}
        </Button>
      </div>
      {approve ? (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={applyNow} onCheckedChange={(v) => setApplyNow(v === true)} className="mt-0.5" />
          <span>{t('changes.applyNow')}</span>
        </label>
      ) : null}
      <ReasonField value={reason} onChange={setReason} />
      {selfApproval ? (
        <>
          <Callout tone="warn" title={t('changes.selfApproveNoticeTitle')}>
            {t('changes.selfApproveNotice')}
          </Callout>
          <Field label={t('changes.totp')} hint={t('changes.totpHint')}>
            <Input value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="w-40 font-mono tracking-widest" />
          </Field>
        </>
      ) : null}
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={change.environment} text={approve === false ? t('changes.confirmReject') : t('changes.confirmApprove')} />
      {decide.state.phase === 'error' ? <IntegrationError error={decide.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={approve === null || !reason.trim() || !confirmed || (selfApproval && totp.length !== 6) || decide.busy || apply.busy} variant={approve === false ? 'destructive' : 'default'}>
          {approve === false ? t('changes.reject') : approve === true && applyNow && !change.stale ? t('changes.approveAndApply') : t('changes.approve')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ApplyForm({ change, onClose, onDone }: { change: IntegrationChange; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: { reason: string | null }, opts) => api.applyIntegrationChange(change.approval.id, body, opts));
  const submit = async () => {
    const res = await write.run({ reason: note.trim() || null });
    if (res) onDone();
  };
  if (write.state.phase === 'done') {
    return (
      <div className="space-y-4">
        <Callout tone="ok" title={t('changes.finished.applied')}>
          {t('outcome.applied', { seconds: ctx.refresh_seconds })}
        </Callout>
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {change.environment ? <EnvironmentBanner env={change.environment} message={t('changes.bannerApply', { seconds: ctx.refresh_seconds })} /> : null}
      <ChangeSummary change={change} />
      <ReasonField value={note} onChange={setNote} optional />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={change.environment} text={t('changes.confirmApply')} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!confirmed || write.busy}>
          <CheckCheck /> {t('changes.apply')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function CancelForm({ change, onClose, onDone }: { change: IntegrationChange; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [reason, setReason] = useState('');
  const write = useWrite((body: { reason: string }, opts) => api.cancelIntegrationChange(change.approval.id, body, opts));
  const submit = async () => {
    const res = await write.run({ reason: reason.trim() });
    if (res) {
      onDone();
      onClose();
    }
  };
  return (
    <div className="space-y-4">
      <ChangeSummary change={change} />
      <ReasonField value={reason} onChange={setReason} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.back')}
        </Button>
        <Button type="button" variant="destructive" onClick={() => void submit()} disabled={!reason.trim() || write.busy}>
          <CircleSlash /> {t('changes.cancelConfirm')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * Open changes relevant to this page (by kind, optionally one connection). Changes that belong to
 * the other integration pages are counted with a link, so nothing waiting is hidden.
 */
export function PendingChangesPanel({
  kinds,
  connectionId,
  tldId,
  module,
  refreshKey,
  onChanged,
}: {
  kinds: readonly ChangeKind[];
  connectionId?: string;
  tldId?: string;
  /** Only the changes of this module instance (Cài đặt › Module). */
  module?: { branch: string; module: string; instance_key: string };
  refreshKey?: number;
  onChanged: () => void;
}) {
  const { t } = useTranslation('integrations');
  const [tick, setTick] = useState(0);
  const result = useApi((signal) => api.listIntegrationChanges({ state: 'OPEN', limit: 200 }, { signal }), [tick, refreshKey]);
  const data = useRetained(result.data);
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('changes.loadFailed')} />;
  if (!data) return <Loading label={t('changes.loading')} />;
  const relevant = data.items.filter(
    (c) =>
      kinds.includes(c.kind) &&
      (!connectionId || c.target.connection?.id === connectionId) &&
      (!tldId || c.target.tld?.id === tldId || (c.target.tlds ?? []).some((x) => x.id === tldId)) &&
      (!module || (c.target.module?.branch === module.branch && c.target.module.module === module.module && c.target.module.instance_key === module.instance_key)),
  );
  const elsewhere = connectionId || tldId || module ? [] : data.items.filter((c) => !kinds.includes(c.kind));
  const otherPages = [...new Set(elsewhere.map((c) => PAGE_OF[c.kind]))];
  const changed = () => {
    setTick((n) => n + 1);
    onChanged();
  };
  if (!relevant.length && !otherPages.length) {
    return <p className="text-sm text-muted-foreground">{t('changes.none')}</p>;
  }
  return (
    <Panel
      title={t('changes.title', { count: relevant.length })}
      description={t('changes.description')}
      className={relevant.some((c) => c.approval.can_decide || c.approval.can_apply) ? 'border-primary/40' : undefined}
    >
      {relevant.length ? (
        <ul className="grid gap-3">
          {relevant.map((c) => (
            <ChangeCard key={c.approval.id} change={c} onChanged={changed} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t('changes.noneHere')}</p>
      )}
      {otherPages.length ? (
        <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{t('changes.elsewhere', { count: elsewhere.length })}</span>
          {otherPages.map((p) => (
            <Link key={p} to={p} className="font-medium text-primary hover:underline">
              {t(`changes.page.${p === '/settings/registries' ? 'registries' : p === '/settings/tlds' ? 'tlds' : p.startsWith('/settings?tab=modules') ? 'modules' : 'vnnic'}`)}
            </Link>
          ))}
        </p>
      ) : null}
    </Panel>
  );
}
